import React, { useState, useRef, useEffect, useMemo } from 'react';
import Select from 'react-select';
import { Bar, Line } from 'react-chartjs-2';
import {
    FINANCIAL_COMPARISONS,
    getComparisonById,
    getComparisonForRatioName,
} from '../config/comparisonConfig';
import { FINANCIAL_METRICS } from '../config/financialMetricRegistry';
import {
    resolveComparisonChart,
    resolveOpenedRatioSeries,
} from '../utils/financialMetricResolver';
import { evaluateComparisonBenchmarks } from '../utils/cashFlowBenchmarkResolver';
import {
    getFinlyticsSelectMenuPortalProps,
    getFinlyticsSelectStyles,
} from '../styles/reactSelectStyles';
import CashFlowBenchmarkPanel from './CashFlowBenchmarkPanel';
import {
    classifyFinancialUnit,
    formatFinancialUnitValue,
    isDerivedChartUnit,
} from '../utils/financialUnit';
import '../styles/ChartModal.css';
import zoomPlugin from 'chartjs-plugin-zoom';

import {
    Chart,
    LineElement,
    BarElement,
    CategoryScale,
    LinearScale,
    PointElement,
    Tooltip,
    Legend,
} from 'chart.js';

Chart.register(
    LineElement,
    BarElement,
    CategoryScale,
    LinearScale,
    PointElement,
    Tooltip,
    Legend,
    zoomPlugin
);

const CURRENT_METRIC = '__current_metric__';
const SELECT_STYLES = getFinlyticsSelectStyles();
const SELECT_MENU_PORTAL_PROPS = getFinlyticsSelectMenuPortalProps();

function isDerivedUnit(unit) {
    return isDerivedChartUnit(unit);
}

function unitFamily(unit) {
    const kind = classifyFinancialUnit(unit);
    if (kind === 'percentage' || kind === 'percentagePoints') return 'percent';
    if (kind === 'multiple') return 'multiple';
    if (kind === 'perShare') return 'perShare';
    if (kind === 'days') return 'days';
    return 'currency';
}

function seriesUsesSecondaryAxis(item, primaryFamily) {
    if (!item || item.role === 'total') return false;
    if (item.role === 'derived' && isDerivedUnit(item.unit)) return true;
    if (primaryFamily && unitFamily(item.unit) !== primaryFamily) return true;
    return false;
}

function seriesCagr(values, windowYears) {
    const valid = (values || []).filter((v) => v != null && v > 0);
    if (valid.length < 2) return null;
    const slice = valid.slice(-windowYears);
    if (slice.length < 2) return null;
    const first = slice[0];
    const last = slice[slice.length - 1];
    if (first <= 0 || last <= 0) return null;
    const yrs = slice.length - 1;
    return Math.pow(last / first, 1 / yrs) - 1;
}

const EPS_DILUTION_CAGR_GAP = 0.03;

/**
 * Format a chart tick or tooltip using existing Indian-number / unit conventions.
 *
 * @param {number|null} value
 * @param {string} [unit]
 * @returns {string}
 */
export function formatFinancialChartValue(value, unit, hints) {
    return formatFinancialUnitValue(value, unit, hints);
}

function comparisonSelectOptions() {
    const groups = new Map();
    FINANCIAL_COMPARISONS.forEach((comparison) => {
        const category = comparison.category || 'Other';
        if (!groups.has(category)) groups.set(category, []);
        groups.get(category).push({
            value: comparison.id,
            label: comparison.label,
        });
    });
    return Array.from(groups.entries()).map(([label, options]) => ({ label, options }));
}

function registryCompareOptions(excludedIds) {
    const excluded = new Set(excludedIds || []);
    const groups = new Map();
    Object.values(FINANCIAL_METRICS).forEach((metric) => {
        if (metric.auditOnly || excluded.has(metric.id)) return;
        const group = metric.statement || metric.type || 'Other';
        if (!groups.has(group)) groups.set(group, []);
        groups.get(group).push({
            value: metric.id,
            label: metric.shortLabel ? `${metric.label} (${metric.shortLabel})` : metric.label,
        });
    });
    return Array.from(groups.entries()).map(([label, options]) => ({ label, options }));
}

const ChartModal = ({ isOpen, ratio, allRatios, financials, companyName, onClose, comparisonId, preferredBasis = 'consolidated', sector }) => {
    const chartRef = useRef(null);
    const [chartType, setChartType] = useState('line');
    const [theme] = useState(() => (document.body.getAttribute('data-theme') === 'light' ? 'light' : 'dark'));
    const [animate, setAnimate] = useState(true);
    const [modalSize, setModalSize] = useState('90%');
    const [selectedKeys, setSelectedKeys] = useState([]);
    const [cagrYears, setCagrYears] = useState(5);
    const [showBaseline, setShowBaseline] = useState(true);
    const [activeComparisonId, setActiveComparisonId] = useState(comparisonId || null);

    const getCssVar = (name, fallback) => {
        const val = getComputedStyle(document.body).getPropertyValue(name).trim();
        return val || fallback;
    };

    const hexToRgba = (hex, alpha) => {
        if (!hex) return `rgba(0, 0, 0, ${alpha})`;
        if (hex.startsWith('rgb')) return hex.replace('rgb(', 'rgba(').replace(')', `, ${alpha})`);
        const clean = hex.replace('#', '');
        const full = clean.length === 3 ? clean.split('').map((c) => c + c).join('') : clean;
        const num = parseInt(full, 16);
        const r = (num >> 16) & 255;
        const g = (num >> 8) & 255;
        const b = num & 255;
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };

    const primaryColor = getCssVar('--chart-blue', '#3B82F6');
    const gridColor = getCssVar('--border-light', theme === 'dark' ? '#333' : '#ddd');
    const textColor = getCssVar('--text-1', theme === 'dark' ? '#fff' : '#000');
    const textMuted = getCssVar('--text-2', theme === 'dark' ? '#ccc' : '#333');

    useEffect(() => {
        const canvas = chartRef.current?.canvas;
        if (!canvas) return;

        canvas.style.cursor = 'grab';

        const onMouseDown = () => {
            canvas.style.cursor = 'grabbing';
            document.body.classList.add('chart-panning');
        };
        const onMouseUp = () => {
            canvas.style.cursor = 'grab';
            document.body.classList.remove('chart-panning');
        };

        canvas.addEventListener('mousedown', onMouseDown);
        window.addEventListener('mouseup', onMouseUp);

        return () => {
            canvas.removeEventListener('mousedown', onMouseDown);
            window.removeEventListener('mouseup', onMouseUp);
        };
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return undefined;
        const onKeyDown = (event) => {
            if (event.key !== 'Escape' || event.defaultPrevented) return;
            event.preventDefault();
            onClose?.();
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [isOpen, onClose]);

    useEffect(() => {
        const mapped = comparisonId || getComparisonForRatioName(ratio?.name)?.id || null;
        setActiveComparisonId(mapped);
        setSelectedKeys([]);
        const nextComparison = mapped ? getComparisonById(mapped) : null;
        if (nextComparison?.chartType === 'stackedBar' || nextComparison?.chartType === 'bar') {
            setChartType('bar');
        } else if (nextComparison?.chartType === 'line') {
            setChartType('line');
        }
    }, [ratio?.name, comparisonId]);

    const activeComparison = activeComparisonId ? getComparisonById(activeComparisonId) : null;

    const comparisonChart = useMemo(() => {
        if (!activeComparison) return null;
        return resolveComparisonChart({
            data: financials,
            comparison: activeComparison,
            allRatios,
            preferredBasis,
            extraMetricIds: selectedKeys,
        });
    }, [activeComparison, financials, allRatios, preferredBasis, selectedKeys]);

    const openedSeries = useMemo(() => {
        if (activeComparison) return null;
        return resolveOpenedRatioSeries(ratio);
    }, [activeComparison, ratio]);

    const benchmarkResult = useMemo(() => {
        if (!activeComparison) return null;
        return evaluateComparisonBenchmarks({
            comparison: activeComparison,
            sector,
            comparisonChart,
            data: financials,
            allRatios,
            preferredBasis,
        });
    }, [activeComparison, sector, comparisonChart, financials, allRatios, preferredBasis]);

    const labels = useMemo(() => (
        activeComparison
            ? (comparisonChart?.displayYears || [])
            : (openedSeries?.displayYears || [])
    ), [activeComparison, comparisonChart, openedSeries]);

    const titleLabel = activeComparison
        ? activeComparison.label
        : (ratio?.displayName || ratio?.name || 'Ratio');

    const cagrValue = useMemo(() => {
        const points = activeComparison
            ? (comparisonChart?.series?.[0]?.values || [])
            : (openedSeries?.values || []);
        return seriesCagr(points, cagrYears);
    }, [activeComparison, comparisonChart, openedSeries, cagrYears]);

    const epsDilutionWarning = useMemo(() => {
        if (activeComparison?.id !== 'net_profit_vs_eps' || !comparisonChart) return null;
        const netProfit = comparisonChart.series.find((item) => item.metricId === 'netProfit');
        const eps = comparisonChart.series.find((item) => item.metricId === 'eps');
        const profitCagr = seriesCagr(netProfit?.values, cagrYears);
        const epsCagr = seriesCagr(eps?.values, cagrYears);
        if (profitCagr == null || epsCagr == null) return null;
        if (profitCagr - epsCagr < EPS_DILUTION_CAGR_GAP) return null;
        const profitPct = (profitCagr * 100).toFixed(2);
        const epsPct = (epsCagr * 100).toFixed(2);
        return `Over the selected CAGR window, EPS CAGR (${epsPct}%) trails net-profit CAGR (${profitPct}%) by 3 percentage points or more — share dilution may be eroding per-share gains.`;
    }, [activeComparison, comparisonChart, cagrYears]);

    const primaryUnitFamily = useMemo(() => {
        const primary = (comparisonChart?.series || []).find((item) => item.role === 'primary');
        return primary ? unitFamily(primary.unit) : null;
    }, [comparisonChart]);

    const hasSecondaryAxis = Boolean(
        activeComparison
        && comparisonChart?.series?.some((item) => seriesUsesSecondaryAxis(item, primaryUnitFamily))
    );

    const isStacked = activeComparison?.chartType === 'stackedBar' && chartType === 'bar';

    const chartData = useMemo(() => {
        const ds = [];

        if (showBaseline) {
            ds.push({
                label: 'Baseline (0)',
                data: labels.map(() => 0),
                borderColor: gridColor,
                borderDash: [4, 4],
                borderWidth: 1,
                pointRadius: 0,
                fill: false,
                spanGaps: false,
                order: 0,
                type: 'line',
                yAxisID: 'y',
            });
        }

        if (activeComparison && comparisonChart) {
            const paletteColors = [
                getCssVar('--chart-series-1', '#EF4444'),
                getCssVar('--chart-series-2', '#10B981'),
                getCssVar('--chart-series-3', '#8B5CF6'),
                getCssVar('--chart-series-4', '#F59E0B'),
                getCssVar('--chart-series-5', '#3B82F6'),
                getCssVar('--chart-series-6', '#EC4899'),
                getCssVar('--chart-series-7', '#14B8A6'),
                getCssVar('--chart-series-8', '#6366F1'),
                getCssVar('--chart-series-9', '#F472B6'),
                getCssVar('--chart-series-10', '#22C55E'),
            ];
            comparisonChart.series.forEach((item, i) => {
                const color = i === 0 ? primaryColor : paletteColors[(i - 1) % paletteColors.length];
                const derived = item.role === 'derived' && isDerivedUnit(item.unit);
                const isTotal = item.role === 'total';
                const datasetType = isTotal || derived || chartType === 'line' ? 'line' : 'bar';
                ds.push({
                    label: item.label,
                    data: item.values,
                    type: datasetType,
                    borderColor: color,
                    backgroundColor: hexToRgba(color, datasetType === 'bar' ? 0.55 : 0.2),
                    borderWidth: 2,
                    fill: false,
                    pointRadius: datasetType === 'line' ? 3 : 0,
                    spanGaps: true,
                    order: isTotal || derived ? 2 : 1,
                    stack: isStacked && item.stack ? item.stack : undefined,
                    yAxisID: seriesUsesSecondaryAxis(item, primaryUnitFamily) && hasSecondaryAxis ? 'y1' : 'y',
                    unit: item.unit,
                    metricName: item.metricId || item.id || '',
                });
            });
        } else if (openedSeries) {
            ds.push({
                label: openedSeries.label,
                data: openedSeries.values,
                borderColor: primaryColor,
                backgroundColor: hexToRgba(primaryColor, 0.2),
                borderWidth: 2,
                fill: false,
                pointRadius: 3,
                spanGaps: true,
                order: 1,
                type: chartType,
                yAxisID: 'y',
                unit: openedSeries.unit,
                metricName: openedSeries.name || '',
            });
        }

        return { labels, datasets: ds };
    }, [
        labels,
        activeComparison,
        comparisonChart,
        openedSeries,
        chartType,
        showBaseline,
        primaryColor,
        gridColor,
        hasSecondaryAxis,
        isStacked,
        primaryUnitFamily,
    ]);

    const chartOptions = useMemo(() => {
        const datasetForAxis = (axisId) => (
            (chartData.datasets || []).find((dataset) => (dataset.yAxisID || 'y') === axisId && (dataset.unit || dataset.metricName))
        );
        const unitForAxis = (axisId) => {
            const match = datasetForAxis(axisId);
            if (match?.unit) return match.unit;
            return axisId === 'y1' ? 'percentage' : '';
        };
        const hintsForAxis = (axisId) => {
            const match = datasetForAxis(axisId);
            return { name: match?.metricName, displayName: match?.label };
        };
        return {
            responsive: true,
            maintainAspectRatio: false,
            animation: animate,
            interaction: { mode: 'nearest', axis: 'x', intersect: false },
            plugins: {
                legend: { display: true },
                valueLabels: false,
                tooltip: {
                    mode: 'index',
                    intersect: false,
                    callbacks: {
                        label: (ctx) => {
                            const unit = ctx.dataset?.unit;
                            const formatted = formatFinancialChartValue(ctx.parsed.y, unit, {
                                name: ctx.dataset?.metricName,
                                displayName: ctx.dataset?.label,
                            });
                            return `${ctx.dataset.label}: ${formatted || '—'}`;
                        },
                    },
                },
                zoom: {
                    zoom: {
                        wheel: { enabled: true },
                        drag: {
                            enabled: true,
                            backgroundColor: hexToRgba(primaryColor, 0.15),
                            borderColor: primaryColor,
                            borderWidth: 1
                        },
                        mode: 'x'
                    },
                    pan: {
                        enabled: true,
                        mode: 'x',
                        modifierKey: 'ctrl'
                    }
                }
            },
            scales: {
                x: {
                    stacked: isStacked,
                    ticks: { color: textColor },
                    grid: { color: gridColor }
                },
                y: {
                    stacked: isStacked,
                    ticks: {
                        color: textColor,
                        callback: (value) => formatFinancialChartValue(value, unitForAxis('y'), hintsForAxis('y')),
                    },
                    grid: { color: gridColor }
                },
                ...(hasSecondaryAxis ? {
                    y1: {
                        position: 'right',
                        stacked: false,
                        ticks: {
                            color: textMuted,
                            callback: (value) => formatFinancialChartValue(value, unitForAxis('y1'), hintsForAxis('y1')),
                        },
                        grid: { drawOnChartArea: false },
                    },
                } : {}),
            }
        };
    }, [animate, chartData.datasets, gridColor, hasSecondaryAxis, isStacked, primaryColor, textColor, textMuted]);

    const ChartComponent = chartType === 'bar' || isStacked ? Bar : Line;
    const hasChartData = labels.length > 0 && (chartData.datasets || []).some((dataset) => (
        dataset.label !== 'Baseline (0)'
        && Array.isArray(dataset.data)
        && dataset.data.some((value) => value != null && Number.isFinite(value))
    ));
    const emptyComparison = Boolean(activeComparison && comparisonChart?.empty);

    const compareOptions = useMemo(() => (
        registryCompareOptions([
            ...(activeComparison?.metricIds || []),
            ...(activeComparison?.derivedMetricId ? [activeComparison.derivedMetricId] : []),
            ...(activeComparison?.totalMetricIds || []),
        ])
    ), [activeComparison]);

    const comparisonOptions = useMemo(() => ([
        { label: 'Current metric', options: [{ value: CURRENT_METRIC, label: ratio?.displayName || ratio?.name || 'Current metric' }] },
        ...comparisonSelectOptions(),
    ]), [ratio?.displayName, ratio?.name]);

    const warningItems = [
        ...(activeComparison?.description ? [activeComparison.description] : []),
        ...((activeComparison?.warnings || []).filter(Boolean)),
        ...(activeComparison?.investorQuestion ? [`Investor question: ${activeComparison.investorQuestion}`] : []),
    ];
    const metricWarnings = (activeComparison?.metricIds || [])
        .map((id) => FINANCIAL_METRICS[id]?.warning)
        .filter(Boolean);
    const panelContent = [
        ...warningItems,
        ...metricWarnings,
        ...(epsDilutionWarning ? [epsDilutionWarning] : []),
    ];

    const handleComparisonChange = (option) => {
        const nextId = option?.value === CURRENT_METRIC ? null : option?.value;
        setActiveComparisonId(nextId || null);
        setSelectedKeys([]);
        const next = nextId ? getComparisonById(nextId) : null;
        if (next?.chartType === 'stackedBar' || next?.chartType === 'bar') setChartType('bar');
        if (next?.chartType === 'line') setChartType('line');
    };

    if (!isOpen || (!ratio && !comparisonId && !activeComparisonId)) return null;

    const handleOverlayMouseDown = (event) => {
        if (event.target === event.currentTarget) onClose?.();
    };

    return (
        <div className="chart-modal-overlay" role="presentation" onMouseDown={handleOverlayMouseDown}>
            <div
                className={`chart-modal-dialog chart-modal-dialog--${String(modalSize).replace('%', '')}`}
                role="dialog"
                aria-modal="true"
                aria-labelledby="chart-modal-title"
            >
                <header className="chart-modal-header">
                    <div className="chart-modal-heading">
                        <h3 id="chart-modal-title">{titleLabel}</h3>
                        {companyName ? <p className="chart-modal-subtitle">{companyName}</p> : null}
                    </div>
                    <div className="chart-modal-header-actions">
                        <label className="chart-modal-control">
                            <span className="chart-modal-control-label">Chart</span>
                            <select
                                className="chart-modal-native-select"
                                value={chartType}
                                onChange={(e) => setChartType(e.target.value)}
                                aria-label="Chart type"
                            >
                                <option value="line">Line</option>
                                <option value="bar">Bar</option>
                            </select>
                        </label>
                        <button
                            type="button"
                            className="chart-modal-close"
                            onClick={onClose}
                            aria-label="Close"
                            title="Close (Esc)"
                        >
                            <span aria-hidden="true">×</span>
                        </button>
                    </div>
                </header>

                {panelContent.length > 0 && (
                    <div className="chart-modal-note" role="note">
                        {panelContent.map((text) => (
                            <p key={text}>{text}</p>
                        ))}
                    </div>
                )}

                <CashFlowBenchmarkPanel result={benchmarkResult} />

                <div className="chart-modal-toolbar">
                    <label className="chart-modal-control">
                        <span className="chart-modal-control-label">Animation</span>
                        <select
                            className="chart-modal-native-select"
                            value={animate}
                            onChange={(e) => setAnimate(e.target.value === 'true')}
                        >
                            <option value="true">On</option>
                            <option value="false">Off</option>
                        </select>
                    </label>
                    <label className="chart-modal-control">
                        <span className="chart-modal-control-label">Size</span>
                        <select
                            className="chart-modal-native-select"
                            value={modalSize}
                            onChange={(e) => setModalSize(e.target.value)}
                        >
                            <option value="50%">50%</option>
                            <option value="80%">80%</option>
                            <option value="90%">90%</option>
                            <option value="100%">100%</option>
                        </select>
                    </label>
                    <label className="chart-modal-control">
                        <span className="chart-modal-control-label">CAGR</span>
                        <select
                            className="chart-modal-native-select"
                            value={cagrYears}
                            onChange={(e) => setCagrYears(Number(e.target.value))}
                            aria-label="CAGR window"
                        >
                            <option value={3}>3 yrs</option>
                            <option value={5}>5 yrs</option>
                            <option value={7}>7 yrs</option>
                            <option value={10}>10 yrs</option>
                        </select>
                        <span className="chart-modal-cagr">
                            {cagrValue != null ? `${(cagrValue * 100).toFixed(2)}%` : 'N/A'}
                        </span>
                    </label>
                    <label className="chart-modal-control chart-modal-control--checkbox">
                        <input
                            type="checkbox"
                            checked={showBaseline}
                            onChange={(e) => setShowBaseline(e.target.checked)}
                        />
                        Baseline
                    </label>
                    <div className="chart-modal-select-row">
                        <span className="chart-modal-control-label">Comparison</span>
                        <Select
                            className="basic-multi-select chart-modal-select"
                            classNamePrefix="select"
                            styles={SELECT_STYLES}
                            options={comparisonOptions}
                            value={
                                activeComparisonId
                                    ? { value: activeComparisonId, label: activeComparison?.label || activeComparisonId }
                                    : { value: CURRENT_METRIC, label: ratio?.displayName || ratio?.name || 'Current metric' }
                            }
                            onChange={handleComparisonChange}
                            placeholder="Select a financial comparison"
                            {...SELECT_MENU_PORTAL_PROPS}
                        />
                    </div>
                    <div className="chart-modal-select-row">
                        <span className="chart-modal-control-label">Compare with</span>
                        <Select
                            isMulti
                            className="basic-multi-select chart-modal-select"
                            classNamePrefix="select"
                            styles={SELECT_STYLES}
                            options={compareOptions}
                            value={compareOptions
                                .flatMap((group) => group.options)
                                .filter((opt) => selectedKeys.includes(opt.value))}
                            onChange={(selectedOptions) => {
                                setSelectedKeys((selectedOptions || []).map((opt) => opt.value));
                            }}
                            placeholder="Add metrics from the registry"
                            {...SELECT_MENU_PORTAL_PROPS}
                        />
                    </div>
                </div>

                <div className="chart-container">
                    {hasChartData && !emptyComparison ? (
                        <ChartComponent key={`${chartType}-${activeComparisonId || 'current'}`} ref={chartRef} data={chartData} options={chartOptions} />
                    ) : (
                        <p className="chart-modal-empty">Chart data not available.</p>
                    )}
                </div>

                <footer className="chart-modal-footer">
                    <button
                        type="button"
                        className="chart-modal-reset"
                        onClick={() => chartRef.current?.resetZoom()}
                    >
                        Reset zoom
                    </button>
                    <span className="chart-modal-hint">
                        Drag to zoom · Ctrl+drag to pan · Scroll to zoom · Esc to close
                    </span>
                </footer>
            </div>
        </div>
    );
};

export default ChartModal;
